import { createContext, useContext, useEffect, useMemo, useReducer } from 'react';
import { produce } from 'immer';

import { useInsightsContext } from './InsightsContext';
import { unimplemented } from '@utils/commonUtils';
import { TableType, TableViewType } from '@insights_module/types/LibraryTable';
import {
  LIBRARY_TABLE_COLUMNS_STORAGE_KEY,
  LIBRARY_TABLE_COLUMNS_STORAGE_KEY_OLD,
  defaultLocalStorageColumns,
} from '@insights_module/hooks/useLibraryTableColumns';
import usePagination from '@app/hooks/usePagination';
import {
  AudioWithDetailsAndGarm,
  GroupWithGarmAndProcessedAudioCount,
} from '@insights_module/types/graphql/Schema';
import { useLibraryTableData } from '@insights_module/hooks/useLibraryTableData';
import { useInsightsParams } from '@insights_module/hooks/useInsightsParams';

type MyLibraryContextKeysType =
  | 'insights.library.search-term'
  | 'insights.library.selected-view'
  | 'insights.library.table-data-type'
  | 'insights.library.audios-table-sort'
  | 'insights.library.groups-table-sort'
  | 'insights.library.audios-columns'
  | 'insights.library.groups-columns'
  | 'insights.library.color-blind-mode';

type SortOrderType = 'asc' | 'desc';

type TableSortType = {
  sortOrder: SortOrderType;
  sortField: string;
  pageNo: string;
};

type ActiveTableColumnsType = string[];

type MyLibraryContextType = {
  selectedView: TableViewType;
  tableType: TableType;
  audiosTableSort: TableSortType;
  groupsTableSort: TableSortType;
  searchTerm: string;
  colorBlindMode: boolean;
  audiosColumns: string[];
  groupsColumns: string[];
  audiosCurrentPage: number;
  audiosHasNextPage: boolean;
  audiosHasPreviousPage: boolean;
  audiosPaginationRange: (string | number)[];
  audiosTotalPages: number;

  groupsCurrentPage: number;
  groupsHasNextPage: boolean;
  groupsHasPreviousPage: boolean;
  groupsPaginationRange: (string | number)[];
  groupsTotalPages: number;

  audiosGoToNextPage: () => void;
  audiosGoToPreviousPage: () => void;
  audiosGoToPage: (pageNumber: number) => void;

  groupsGoToNextPage: () => void;
  groupsGoToPreviousPage: () => void;
  groupsGoToPage: (pageNumber: number) => void;

  setSelectedView: (val: TableViewType) => void;
  setAudiosTableSort: (val: TableSortType) => void;
  setGroupsTableSort: (val: TableSortType) => void;
  setSearchTerm: (val: string) => void;
  setAudiosColumn: (val: string | string[]) => void;
  setGroupsColumn: (val: string | string[]) => void;
  setColorBlindMode: (val: boolean) => void;
};

type Action = {
  type: MyLibraryContextKeysType;
  payload:
    | string
    | string[]
    | boolean
    | TableViewType
    | TableType
    | TableSortType
    | ActiveTableColumnsType;
};

const MY_LIBRARY_CONTEXT_KEYS = {
  SELECTED_VIEW: 'insights.library.selected-view' as MyLibraryContextKeysType,
  TABLE_DATA_TYPE: 'insights.library.table-data-type' as MyLibraryContextKeysType,
  SEARCH_TERM: 'insights.library.search-term' as MyLibraryContextKeysType,
  AUDIOS_TABLE_SORT: 'insights.library.audios-table-sort' as MyLibraryContextKeysType,
  GROUPS_TABLE_SORT: 'insights.library.groups-table-sort' as MyLibraryContextKeysType,
  AUDIOS_COLUMNS: 'insights.library.audios-columns' as MyLibraryContextKeysType,
  GROUPS_COLUMNS: 'insights.library.groups-columns' as MyLibraryContextKeysType,
  COLOR_BLIND_MODE: 'insights.library.color-blind-mode' as MyLibraryContextKeysType,
};

const localStorageData: string | null = window.localStorage.getItem(
  LIBRARY_TABLE_COLUMNS_STORAGE_KEY
);

// removing old localStorage key data
// TODO: remove this after a month of releasing the new version
const localStorageDataOld: string | null = window.localStorage.getItem(
  LIBRARY_TABLE_COLUMNS_STORAGE_KEY_OLD
);
if (localStorageDataOld) {
  localStorage.removeItem(LIBRARY_TABLE_COLUMNS_STORAGE_KEY_OLD);
}

const initialState: MyLibraryContextType = {
  selectedView: 'contextual',
  tableType: 'audios',
  searchTerm: '',
  audiosTableSort: { sortOrder: 'asc', sortField: 'title', pageNo: '1' },
  groupsTableSort: { sortOrder: 'asc', sortField: 'name', pageNo: '1' },
  audiosColumns: localStorageData
    ? JSON.parse(localStorageData)?.audios
    : defaultLocalStorageColumns.audios,
  groupsColumns: localStorageData
    ? JSON.parse(localStorageData)?.groups
    : defaultLocalStorageColumns.groups,

  audiosCurrentPage: 1,
  audiosHasNextPage: false,
  audiosHasPreviousPage: false,
  audiosPaginationRange: [],
  audiosTotalPages: 1,

  colorBlindMode: false,

  groupsCurrentPage: 1,
  groupsHasNextPage: false,
  groupsHasPreviousPage: false,
  groupsPaginationRange: [],
  groupsTotalPages: 1,

  audiosGoToNextPage: unimplemented,
  audiosGoToPreviousPage: unimplemented,
  audiosGoToPage: unimplemented,

  groupsGoToNextPage: unimplemented,
  groupsGoToPreviousPage: unimplemented,
  groupsGoToPage: unimplemented,

  setSelectedView: unimplemented,
  setAudiosTableSort: unimplemented,
  setGroupsTableSort: unimplemented,
  setSearchTerm: unimplemented,
  setAudiosColumn: unimplemented,
  setGroupsColumn: unimplemented,
  setColorBlindMode: unimplemented,
};

const MyLibraryContext = createContext(initialState);

const reducer = produce((draft: MyLibraryContextType, action: Action) => {
  const { type, payload } = action;
  switch (type) {
    case MY_LIBRARY_CONTEXT_KEYS.SELECTED_VIEW:
      draft.selectedView = payload as TableViewType;
      break;

    case MY_LIBRARY_CONTEXT_KEYS.TABLE_DATA_TYPE:
      draft.tableType = payload as TableType;
      break;

    case MY_LIBRARY_CONTEXT_KEYS.AUDIOS_TABLE_SORT:
      draft.audiosTableSort = payload as TableSortType;
      break;

    case MY_LIBRARY_CONTEXT_KEYS.GROUPS_TABLE_SORT:
      draft.groupsTableSort = payload as TableSortType;
      break;

    case MY_LIBRARY_CONTEXT_KEYS.SEARCH_TERM:
      draft.searchTerm = payload as string;
      break;

    case MY_LIBRARY_CONTEXT_KEYS.AUDIOS_COLUMNS:
      if (Array.isArray(payload)) {
        draft.audiosColumns = payload as string[];
      } else {
        if (draft.audiosColumns?.includes(payload as string)) {
          draft.audiosColumns = draft.audiosColumns.filter((item) => item !== payload);
        } else {
          draft.audiosColumns = [...draft.audiosColumns, payload as string];
        }
      }
      localStorage.setItem(
        LIBRARY_TABLE_COLUMNS_STORAGE_KEY,
        JSON.stringify({ groups: draft.groupsColumns, audios: draft.audiosColumns })
      );
      break;

    case MY_LIBRARY_CONTEXT_KEYS.GROUPS_COLUMNS:
      if (Array.isArray(payload)) {
        draft.groupsColumns = payload;
      } else {
        if (draft.groupsColumns?.includes(payload as string)) {
          draft.groupsColumns = draft.groupsColumns.filter((item) => item !== payload);
        } else {
          draft.groupsColumns = [...draft.groupsColumns, payload as string];
        }
      }
      localStorage.setItem(
        LIBRARY_TABLE_COLUMNS_STORAGE_KEY,
        JSON.stringify({ groups: draft.groupsColumns, audios: draft.audiosColumns })
      );
      break;

    case MY_LIBRARY_CONTEXT_KEYS.COLOR_BLIND_MODE:
      draft.colorBlindMode = payload as boolean;
      break;

    default:
      return draft;
  }
});

export const MyLibraryProvider = ({
  children,
  tableType,
}: {
  children: React.ReactNode;
  tableType: TableType;
}) => {
  const { query } = useInsightsParams();
  const [state, dispatch] = useReducer(reducer, initialState);
  const { audiosTableSort, groupsTableSort } = state;
  const { setSelectedTableType, setSelectedShow, setSelectedEpisode } = useInsightsContext();
  const { audiosData, totalAudiosCount, groupsData, totalGroupsCount } = useLibraryTableData();

  const isGroupsTable = useMemo(() => tableType === 'groups', [tableType]);

  const {
    currentPage: audiosCurrentPage,
    paginationRange: audiosPaginationRange,
    totalPages: audiosTotalPages,
    goToNextPage: audiosGoToNextPage,
    goToPreviousPage: audiosGoToPreviousPage,
    goToPage: audiosGoToPage,
    hasNextPage: audiosHasNextPage,
    hasPreviousPage: audiosHasPreviousPage,
  } = usePagination<AudioWithDetailsAndGarm>({
    data: audiosData || [],
    itemsPerPage: 20,
    totalCount: totalAudiosCount,
    filter: JSON.stringify(query),
  });

  const {
    currentPage: groupsCurrentPage,
    paginationRange: groupsPaginationRange,
    totalPages: groupsTotalPages,
    goToNextPage: groupsGoToNextPage,
    goToPreviousPage: groupsGoToPreviousPage,
    goToPage: groupsGoToPage,
    hasNextPage: groupsHasNextPage,
    hasPreviousPage: groupsHasPreviousPage,
  } = usePagination<GroupWithGarmAndProcessedAudioCount>({
    data: groupsData || [],
    itemsPerPage: 20,
    totalCount: totalGroupsCount,
    filter: JSON.stringify(query),
  });

  useEffect(() => {
    setTableType(tableType);
  }, [tableType]);

  useEffect(() => {
    if (!isGroupsTable) {
      setAudiosTableSort({ ...audiosTableSort, pageNo: String(audiosCurrentPage) });
    }
  }, [audiosCurrentPage, isGroupsTable]);

  useEffect(() => {
    if (isGroupsTable) {
      setGroupsTableSort({ ...groupsTableSort, pageNo: String(groupsCurrentPage) });
    }
  }, [groupsCurrentPage, isGroupsTable]);

  // we need to set the table type in the insights context to match the table type in the my library context
  // because many elements depend on the table type in the insights context
  // in the future, we should refactor the app to not depend on the table type in the insights context
  // or remove table type from the my library context
  useEffect(() => {
    setSelectedTableType(tableType === 'audios' ? 'episodes' : 'shows');
    setSelectedShow('');
    setSelectedEpisode('');
  }, [tableType]);

  const setSelectedView = (payload: TableViewType) => {
    dispatch({ type: MY_LIBRARY_CONTEXT_KEYS.SELECTED_VIEW, payload });
  };

  const setTableType = (payload: TableType) => {
    dispatch({ type: MY_LIBRARY_CONTEXT_KEYS.TABLE_DATA_TYPE, payload });
  };

  const setAudiosTableSort = (payload: TableSortType) => {
    dispatch({ type: MY_LIBRARY_CONTEXT_KEYS.AUDIOS_TABLE_SORT, payload });
  };

  const setGroupsTableSort = (payload: TableSortType) => {
    dispatch({ type: MY_LIBRARY_CONTEXT_KEYS.GROUPS_TABLE_SORT, payload });
  };

  const setSearchTerm = (payload: string) => {
    dispatch({ type: MY_LIBRARY_CONTEXT_KEYS.SEARCH_TERM, payload });
  };

  const setAudiosColumn = (payload: string | string[]) => {
    dispatch({ type: MY_LIBRARY_CONTEXT_KEYS.AUDIOS_COLUMNS, payload });
  };

  const setGroupsColumn = (payload: string | string[]) => {
    dispatch({ type: MY_LIBRARY_CONTEXT_KEYS.GROUPS_COLUMNS, payload });
  };

  const setColorBlindMode = (payload: boolean) => {
    dispatch({ type: MY_LIBRARY_CONTEXT_KEYS.COLOR_BLIND_MODE, payload });
  };

  const values = {
    ...state,
    setSelectedView,
    setAudiosTableSort,
    setGroupsTableSort,
    setSearchTerm,
    setAudiosColumn,
    setGroupsColumn,

    audiosCurrentPage,
    audiosHasNextPage,
    audiosHasPreviousPage,
    audiosPaginationRange,
    audiosTotalPages,
    audiosGoToNextPage,
    audiosGoToPreviousPage,
    audiosGoToPage,

    groupsCurrentPage,
    groupsHasNextPage,
    groupsHasPreviousPage,
    groupsPaginationRange,
    groupsTotalPages,
    groupsGoToNextPage,
    groupsGoToPreviousPage,
    groupsGoToPage,

    setColorBlindMode,
  };

  return <MyLibraryContext.Provider value={values}>{children}</MyLibraryContext.Provider>;
};

export const useMyLibraryContext = () => useContext(MyLibraryContext);
